import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject, from, of } from 'rxjs';
import { catchError, map, mergeMap, takeUntil } from 'rxjs/operators';
import { MpdAlbum, MPDService, MpdStatus } from 'src/app/services/mpd.service';

@Component({
  selector: 'app-music',
  templateUrl: './music.component.html',
  styleUrls: ['./music.component.scss']
})
export class MusicComponent implements OnInit, OnDestroy {
  public status: MpdStatus | null = null;
  public albums: MpdAlbum[] = [];
  public coverUrls: Record<string, string> = {};
  public activeAlbumId: string | null = null;
  public pendingAlbumId: string | null = null;
  public statusLoading = false;
  public albumsLoading = false;
  public controlInProgress = false;
  public playerError: string | null = null;
  public libraryError: string | null = null;

  private pollTimer: number | null = null;
  private readonly destroy$ = new Subject<void>();
  private readonly cancelCoverLoad$ = new Subject<void>();

  constructor(private mpdService: MPDService) {}

  ngOnInit(): void {
    this.refreshStatus();
    this.loadAlbums();
    this.pollTimer = window.setInterval(() => this.refreshStatus(false), 5000);
  }

  ngOnDestroy(): void {
    if (this.pollTimer !== null) {
      window.clearInterval(this.pollTimer);
    }

    this.cancelCoverLoad$.next();
    this.cancelCoverLoad$.complete();
    this.destroy$.next();
    this.destroy$.complete();
    this.revokeCoverUrls();
  }

  get isConnected(): boolean {
    return Boolean(this.status?.connected);
  }

  get isPlaying(): boolean {
    return this.status?.state === 'play';
  }

  get canControlPlayback(): boolean {
    return this.isConnected && (this.status?.playlistLength ?? 0) > 0;
  }

  get canSkipTrack(): boolean {
    return this.isConnected && (this.status?.playlistLength ?? 0) > 1;
  }

  get playbackLabel(): string {
    if (!this.isConnected) {
      return 'Disconnected';
    }

    if (this.status?.state === 'play') {
      return 'Playing';
    }

    if (this.status?.state === 'pause') {
      return 'Paused';
    }

    return 'Stopped';
  }

  get playbackProgress(): number {
    const elapsed = this.status?.elapsed ?? 0;
    const duration = this.status?.duration ?? 0;
    return duration > 0 ? Math.max(0, Math.min(100, (elapsed / duration) * 100)) : 0;
  }

  get currentAlbum(): MpdAlbum | null {
    return this.albums.find(album => album.id === this.activeAlbumId) ?? null;
  }

  get currentCoverUrl(): string | null {
    return this.activeAlbumId ? this.coverUrls[this.activeAlbumId] ?? null : null;
  }

  public refreshStatus(showLoading = true): void {
    if (this.statusLoading || this.controlInProgress) {
      return;
    }

    this.statusLoading = true;
    if (showLoading) {
      this.playerError = null;
    }

    this.mpdService.getStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (status) => {
          this.statusLoading = false;
          this.playerError = null;
          this.applyStatus(status);
        },
        error: (error) => {
          this.statusLoading = false;
          this.status = null;
          this.activeAlbumId = null;
          this.playerError = this.getErrorMessage(error, 'Unable to connect to Music Player Daemon.');
        }
      });
  }

  public loadAlbums(): void {
    if (this.albumsLoading) {
      return;
    }

    this.albumsLoading = true;
    this.libraryError = null;
    this.cancelCoverLoad$.next();
    this.revokeCoverUrls();

    this.mpdService.getAlbums()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (albums) => {
          this.albumsLoading = false;
          this.albums = albums;
          this.updateActiveAlbum();
          this.loadAlbumCovers(albums);
        },
        error: (error) => {
          this.albumsLoading = false;
          this.albums = [];
          this.libraryError = this.getErrorMessage(error, 'Unable to load the music library.');
        }
      });
  }

  public togglePlayback(): void {
    if (!this.canControlPlayback) {
      return;
    }

    this.runControl(this.mpdService.toggle());
  }

  public playPrevious(): void {
    if (!this.canSkipTrack) {
      return;
    }

    this.runControl(this.mpdService.previous());
  }

  public playNext(): void {
    if (!this.canSkipTrack) {
      return;
    }

    this.runControl(this.mpdService.next());
  }

  public playAlbum(album: MpdAlbum): void {
    if (this.pendingAlbumId || this.controlInProgress) {
      return;
    }

    this.pendingAlbumId = album.id;
    this.controlInProgress = true;
    this.playerError = null;

    this.mpdService.playAlbum(album.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (status) => {
          this.pendingAlbumId = null;
          this.controlInProgress = false;
          this.applyStatus(status);
        },
        error: (error) => {
          this.pendingAlbumId = null;
          this.controlInProgress = false;
          this.playerError = this.getErrorMessage(error, `Unable to play ${album.name}.`);
        }
      });
  }

  public formatTime(seconds: number | null | undefined): string {
    if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) {
      return '--:--';
    }

    const totalSeconds = Math.max(0, Math.floor(seconds));
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  public albumTrackLabel(album: MpdAlbum): string {
    return album.trackCount === 1 ? '1 track' : `${album.trackCount} tracks`;
  }

  private runControl(request: Observable<MpdStatus>): void {
    if (this.controlInProgress) {
      return;
    }

    this.controlInProgress = true;
    this.playerError = null;

    request.pipe(takeUntil(this.destroy$)).subscribe({
      next: (status) => {
        this.controlInProgress = false;
        this.applyStatus(status);
      },
      error: (error) => {
        this.controlInProgress = false;
        this.playerError = this.getErrorMessage(error, 'Unable to control playback.');
      }
    });
  }

  private applyStatus(status: MpdStatus): void {
    this.status = status;
    this.playerError = null;
    this.updateActiveAlbum();
  }

  private updateActiveAlbum(): void {
    const currentFile = (this.status?.song?.file || '').replace(/\\/g, '/');
    const activeAlbum = this.albums.find(album => (
      currentFile === album.relativePath || currentFile.startsWith(`${album.relativePath}/`)
    ));

    this.activeAlbumId = activeAlbum?.id ?? null;
  }

  private loadAlbumCovers(albums: MpdAlbum[]): void {
    from(albums.filter(album => album.hasCover))
      .pipe(
        mergeMap(album => this.mpdService.getAlbumCover(album.id).pipe(
          map(blob => ({ albumId: album.id, url: URL.createObjectURL(blob) })),
          catchError(() => of(null))
        ), 6),
        takeUntil(this.cancelCoverLoad$),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        if (!result) {
          return;
        }

        this.coverUrls[result.albumId] = result.url;
      });
  }

  private revokeCoverUrls(): void {
    Object.values(this.coverUrls).forEach(url => URL.revokeObjectURL(url));
    this.coverUrls = {};
  }

  private getErrorMessage(error: any, fallback: string): string {
    return error?.error?.detail
      ?? error?.error?.error
      ?? error?.message
      ?? fallback;
  }
}
